import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { projectsAPI } from '../api';
import { Project, CreateProjectRequest, UpdateProjectRequest } from '../types/api';
import { useAuth } from '../contexts/AuthContext';

// Query keys
const QUERY_KEYS = {
  projects: ['projects'] as const,
  project: (id: string) => ['projects', id] as const,
};

// Get all projects
export function useProjects() {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: QUERY_KEYS.projects,
    queryFn: async () => {
      const response = await projectsAPI.getProjects();
      const projects = response.data?.projects;
      return Array.isArray(projects) ? projects : [];
    },
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 5, // 5 minutes
    placeholderData: [], // Provide empty array as placeholder during refetch
  });
}

// Get single project
export function useProject(projectId: string) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: QUERY_KEYS.project(projectId),
    queryFn: async () => {
      const response = await projectsAPI.getProject(projectId);
      return response.data;
    },
    enabled: isAuthenticated && Boolean(projectId),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Create project mutation
export function useCreateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateProjectRequest) => {
      console.log('🔍 [useCreateProject] Creating project with data:', data);
      return projectsAPI.createProject(data);
    },
    onSuccess: (response) => {
      console.log('🔍 [useCreateProject] Project creation success, response:', response);
      console.log('🔍 [useCreateProject] Current cache before invalidation:', queryClient.getQueryData(QUERY_KEYS.projects));
      
      // Simply invalidate and refetch to avoid cache structure issues
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.projects });
      console.log('🔍 [useCreateProject] Cache invalidated');

      // Projects are free to create, no credit deduction needed

      return response.data;
    },
    onError: (error) => {
      console.error('🔍 [useCreateProject] Error creating project:', error);
    },
  });
}

// Update project mutation
export function useUpdateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ 
      projectId, 
      data 
    }: { 
      projectId: string; 
      data: UpdateProjectRequest 
    }) => projectsAPI.updateProject(projectId, data),
    onSuccess: (response, variables) => {
      // Update the specific project in cache
      queryClient.setQueryData(
        QUERY_KEYS.project(variables.projectId),
        response.data
      );

      // Invalidate projects list to ensure consistency
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.projects });

      return response.data;
    },
  });
}

// Delete project mutation
export function useDeleteProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (projectId: string) => projectsAPI.deleteProject(projectId),
    onSuccess: (_, projectId) => {
      // Remove project from individual cache
      queryClient.removeQueries({ queryKey: QUERY_KEYS.project(projectId) });

      // Invalidate and refetch projects list to avoid cache structure issues
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.projects });
    },
  });
}